import { useEffect, useRef, useState, useCallback } from "react";

/** Smoothly animate a number from previous value to next value. */
export function useCountUp(value: number, durationMs = 600): number {
  const [display, setDisplay] = useState(value);
  const fromRef = useRef(value);
  const startRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (display === value) return;
    fromRef.current = display;
    startRef.current = null;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setDisplay(value);
      return;
    }
    const tick = (ts: number) => {
      if (startRef.current === null) startRef.current = ts;
      const elapsed = ts - startRef.current;
      const t = Math.min(1, elapsed / durationMs);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(fromRef.current + (value - fromRef.current) * eased);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [value, durationMs]);

  return display;
}

/**
 * Auto-refresh visible data on interval, visibility restore, and window focus.
 * Usage: useAutoRefresh(() => loadData(), 30000);
 */
export function useAutoRefresh(fn: () => void, intervalMs = 30000) {
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    let lastRefreshAt = 0;
    const refreshIfVisible = () => {
      if (document.hidden) return;
      const now = Date.now();
      if (now - lastRefreshAt < 750) return;
      lastRefreshAt = now;
      fnRef.current();
    };
    const id = setInterval(refreshIfVisible, intervalMs);
    const onVisibilityChange = () => {
      if (!document.hidden) refreshIfVisible();
    };
    window.addEventListener("focus", refreshIfVisible);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", refreshIfVisible);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [intervalMs]);
}

/**
 * Listen for global "app:resync" event (fired after sync button completes).
 * Usage: useResyncListener(() => refetchData());
 */
export function useResyncListener(fn: () => void) {
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    const handler = () => fnRef.current();
    window.addEventListener("app:resync", handler);
    return () => window.removeEventListener("app:resync", handler);
  }, []);
}
